// ── useTradeShop.js ────────────────────────────────────────────────────────────
// Manages the Trade Outpost economy:
//   - Generates a dynamic shop inventory each time the player docks
//   - Handles buy (VC → items/materials) and sell (items/materials → VC)
//   - Applies ±15% price variance per sector to encourage buy-low strategies
//   - 24h shop reset cooldown (reducible via research)
//   - Discovery item categories (unlocked via research)
// ─────────────────────────────────────────────────────────────────────────────

import { useMemo, useCallback, useState, useEffect } from "react";
import { DEBRIS_ITEMS, TIER1_MATERIALS, TIER2_MATERIALS, TIER3_MATERIALS } from "../data/items";
import { mulberry32 } from "../utils/sector";

// ── Base prices (in Void-Creds) ───────────────────────────────────────────────
const RAW_BASE_PRICES = {
  wire:     4,  glass:   5,  plating:  4,
  chip:     9,  pipe:    6,  lens:    12,
  cable:    5,  panel:  15,  coil:   18,
  cell:    14,  core:   35,  shard:  45,
  filament: 30,
};

const MAT_BASE_PRICES = {
  metal:      18,  crystal:    25,  composite: 32,
  energy:     40,  optics:     35,
  alloy:      90,  nano:      110,  plasma_cell: 105,
  darksteel: 220,  singularity: 280,
};

// ── Discovery item pools ──────────────────────────────────────────────────────
// "discovery" = exotic one-off items buyable only via the trade network.
// These are always buy-only (cannot be sold back).

const DISCOVERY_RAW = [
  { id: "disc_void_filament",  name: "Void Filament Sample",  icon: "🕸️", color: "#66ffee", buyPrice: 120, category: "discovery", desc: "Exotic nano-thread spun from collapsed rift matter." },
  { id: "disc_plasma_shard",   name: "Plasma Shard",          icon: "🌋", color: "#ff6633", buyPrice: 95,  category: "discovery", desc: "Crystallised plasma ejected from a stellar event." },
  { id: "disc_echo_crystal",   name: "Echo Crystal",          icon: "🔮", color: "#cc88ff", buyPrice: 180, category: "discovery", desc: "A crystal that resonates with rift frequencies." },
  { id: "disc_dark_ore",       name: "Dark Ore Fragment",     icon: "⚫", color: "#445566", buyPrice: 140, category: "discovery", desc: "Dense unknown ore from a collapsed void pocket." },
  { id: "disc_ion_residue",    name: "Ion Residue",           icon: "⚡", color: "#aaddff", buyPrice: 75,  category: "discovery", desc: "Charged particle residue from an ionic storm." },
];

const DISCOVERY_REFINED = [
  { id: "disc_stellar_alloy",  name: "Stellar Alloy",         icon: "⭐", color: "#ffdd44", buyPrice: 320, category: "discovery", desc: "An alloy forged under extreme stellar pressure." },
  { id: "disc_rift_weave",     name: "Rift-Woven Composite",  icon: "🌀", color: "#9966dd", buyPrice: 280, category: "discovery", desc: "Composite material threaded with rift energy." },
  { id: "disc_nano_circuit",   name: "Nano Circuit Matrix",   icon: "🔲", color: "#44dd88", buyPrice: 350, category: "discovery", desc: "A self-assembling nano-circuit lattice." },
  { id: "disc_void_core",      name: "Void Core Extract",     icon: "🌑", color: "#cc44aa", buyPrice: 420, category: "discovery", desc: "Concentrated energy extracted from a void core." },
];

const DISCOVERY_EXOTIC = [
  { id: "disc_singularity_shard", name: "Singularity Shard",  icon: "🌑", color: "#cc88ff", buyPrice: 1200, category: "discovery", desc: "A fragment of a collapsed singularity — immeasurable density." },
  { id: "disc_rift_anchor",       name: "Rift Anchor",         icon: "⚓", color: "#aa55ff", buyPrice: 900,  category: "discovery", desc: "A device that stabilises rift tears — only three are known to exist." },
  { id: "disc_dark_matter_vial",  name: "Dark Matter Vial",    icon: "🧪", color: "#3344cc", buyPrice: 1500, category: "discovery", desc: "Contained dark matter — theoretically impossible to bottle." },
];

// Base sell ratio (players can't arbitrage for free)
const BASE_SELL_RATIO = 0.45;

// ── Price fluctuation ─────────────────────────────────────────────────────────
function sectorPriceMultiplier(itemId, sectorIndex) {
  const seed = sectorIndex * 9999 + itemId.split("").reduce((a, c) => a + c.charCodeAt(0), 0);
  const rng  = mulberry32(seed);
  return 0.85 + rng() * 0.30;  // ±15% around 1.0
}

// ── Shop item pool ─────────────────────────────────────────────────────────────
export function generateShopItems(sectorIndex, nodeId, unlocks = {}, stockMult = 1) {
  const rng = mulberry32(sectorIndex * 31337 + nodeId * 997 + 42);

  const items = [];

  // ── Raw salvage ──────────────────────────────────────────────────────────
  const rawPool = DEBRIS_ITEMS.filter(d => {
    if (d.rarity >= 0.8)  return true;
    if (d.rarity >= 0.4)  return sectorIndex >= 1;
    return sectorIndex >= 3;
  });

  for (const item of rawPool) {
    const mult      = sectorPriceMultiplier(item.id, sectorIndex);
    const base      = RAW_BASE_PRICES[item.id] ?? 5;
    const buyPrice  = Math.round(base * mult);
    const sellPrice = Math.round(buyPrice * BASE_SELL_RATIO);
    const baseStock = item.rarity >= 0.8
      ? 20 + Math.floor(rng() * 30)
      : item.rarity >= 0.5
        ? 5  + Math.floor(rng() * 10)
        : 2  + Math.floor(rng() * 4);
    const stock = Math.max(1, Math.round(baseStock * stockMult));
    items.push({ ...item, buyPrice, sellPrice, stock, category: "raw" });
  }

  // ── Smelted materials ────────────────────────────────────────────────────
  const matPool = [
    ...(sectorIndex >= 0 ? TIER1_MATERIALS : []),
    ...(sectorIndex >= 3 ? TIER2_MATERIALS : []),
    ...(sectorIndex >= 6 ? TIER3_MATERIALS : []),
  ];

  for (const mat of matPool) {
    const mult      = sectorPriceMultiplier(mat.id, sectorIndex);
    const base      = MAT_BASE_PRICES[mat.id] ?? 30;
    const buyPrice  = Math.round(base * mult);
    const sellPrice = Math.round(buyPrice * BASE_SELL_RATIO);
    const baseStock = mat.tier === 1
      ? 4 + Math.floor(rng() * 6)
      : mat.tier === 2
        ? 2 + Math.floor(rng() * 3)
        : 1 + Math.floor(rng() * 2);
    const stock = Math.max(1, Math.round(baseStock * stockMult));
    items.push({ ...mat, buyPrice, sellPrice, stock, category: "material" });
  }

  // ── Discovery items ──────────────────────────────────────────────────────
  // Each outpost picks a random subset of available discovery items.
  // Discovery items: no sell price (buy-only)

  if (unlocks.discovery_raw) {
    // Pick 2-3 raw discovery items seeded per outpost
    const pool = [...DISCOVERY_RAW];
    const count = 2 + Math.floor(rng() * 2);
    for (let i = 0; i < count && pool.length; i++) {
      const idx = Math.floor(rng() * pool.length);
      const item = pool.splice(idx, 1)[0];
      const mult     = sectorPriceMultiplier(item.id, sectorIndex);
      const buyPrice = Math.round(item.buyPrice * mult);
      items.push({ ...item, buyPrice, sellPrice: 0, stock: 1 + Math.floor(rng() * 2) });
    }
  }

  if (unlocks.discovery_refined) {
    const pool = [...DISCOVERY_REFINED];
    const count = 1 + Math.floor(rng() * 2);
    for (let i = 0; i < count && pool.length; i++) {
      const idx = Math.floor(rng() * pool.length);
      const item = pool.splice(idx, 1)[0];
      const mult     = sectorPriceMultiplier(item.id, sectorIndex);
      const buyPrice = Math.round(item.buyPrice * mult);
      items.push({ ...item, buyPrice, sellPrice: 0, stock: 1 });
    }
  }

  if (unlocks.discovery_exotic && sectorIndex >= 5) {
    // Only one exotic item per outpost, and only deep sectors
    const pool = [...DISCOVERY_EXOTIC];
    const idx  = Math.floor(rng() * pool.length);
    const item = pool[idx];
    items.push({ ...item, buyPrice: item.buyPrice, sellPrice: 0, stock: 1 });
  }

  return items;
}

// ── Cooldown helpers ──────────────────────────────────────────────────────────
const COOLDOWN_KEY = "voidgalaxy_trade_reset";

function getLastReset() {
  try { return parseInt(localStorage.getItem(COOLDOWN_KEY) || "0", 10); }
  catch { return 0; }
}

function setLastReset(ts) {
  try { localStorage.setItem(COOLDOWN_KEY, String(ts)); }
  catch { /* ignore */ }
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useTradeShop({
  sectorIndex, currentNode,
  voidCreds, setVoidCreds,
  inventory, setInventory,
  materials, setMaterials,
  stats = {},
  unlocks = {},
}) {
  // ── Cooldown state ────────────────────────────────────────────────────────
  const [shopSeed, setShopSeed] = useState(() => currentNode);
  const [lastReset, setLastResetState] = useState(getLastReset);
  const [now, setNow] = useState(Date.now);

  // Tick every 10s to update the countdown display
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 10000);
    return () => clearInterval(id);
  }, []);

  const cooldownMs   = (stats.tradeCooldownHours ?? 24) * 3_600_000;
  const nextResetAt  = lastReset + cooldownMs;
  const msRemaining  = Math.max(0, nextResetAt - now);
  const canReset     = msRemaining === 0;

  // Format remaining time as "Xh Ym" or "Ym" or "Ready"
  const resetCountdown = canReset ? "Ready" : (() => {
    const totalMin = Math.ceil(msRemaining / 60000);
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  })();

  // Reset the shop: pick a new seed and record timestamp
  const resetShop = useCallback(() => {
    if (!canReset && (stats.tradeCooldownHours ?? 24) > 0) return;
    const ts = Date.now();
    setLastReset(ts);
    setLastResetState(ts);
    setNow(ts);
    setShopSeed(v => v + 100000 + Math.floor(Math.random() * 9999));
  }, [canReset, stats.tradeCooldownHours]);

  // ── Shop inventory ────────────────────────────────────────────────────────
  // Always generate items — isTradeNode only controls whether the tab is
  // reachable via normal navigation, but dev mode bypasses that.
  const stockMult = stats.tradeStockMult ?? 1;
  const shopItems = useMemo(
    () => generateShopItems(sectorIndex, shopSeed, unlocks, stockMult),
    [sectorIndex, shopSeed, unlocks, stockMult]
  );

  // Apply buy price discount from research
  const buyMult = stats.tradeBuyMult ?? 1;
  const sellBonus = stats.tradeSellBonus ?? 0;
  const displayItems = useMemo(() =>
    shopItems.map(item => ({
      ...item,
      buyPrice:  item.category === "discovery" ? item.buyPrice : Math.max(1, Math.round(item.buyPrice * buyMult)),
      sellPrice: item.sellPrice > 0 ? Math.max(1, Math.round(item.sellPrice * (1 + sellBonus / BASE_SELL_RATIO))) : 0,
    })),
    [shopItems, buyMult, sellBonus]
  );

  // How many of each item the player currently owns
  const getOwned = useCallback((item) => {
    if (item.category === "raw")      return inventory[item.id] || 0;
    if (item.category === "material") return materials[item.id] || 0;
    if (item.category === "discovery") return inventory[item.id] || 0;
    return 0;
  }, [inventory, materials]);

  // ── Buy ───────────────────────────────────────────────────────────────────
  const buyItem = useCallback((item, qty = 1) => {
    const totalCost = item.buyPrice * qty;
    if (voidCreds < totalCost) return false;
    if (item.stock < qty)      return false;

    setVoidCreds(v => v - totalCost);

    if (item.category === "raw" || item.category === "discovery") {
      setInventory(inv => ({ ...inv, [item.id]: (inv[item.id] || 0) + qty }));
    } else {
      setMaterials(m => ({ ...m, [item.id]: (m[item.id] || 0) + qty }));
    }
    return true;
  }, [voidCreds, setVoidCreds, setInventory, setMaterials]);

  // ── Sell ──────────────────────────────────────────────────────────────────
  const sellItem = useCallback((item, qty = 1) => {
    if (item.sellPrice === 0) return false;  // discovery items not sellable
    const owned = getOwned(item);
    if (owned < qty) return false;

    const totalGain = item.sellPrice * qty;
    setVoidCreds(v => v + totalGain);

    if (item.category === "raw") {
      setInventory(inv => ({ ...inv, [item.id]: (inv[item.id] || 0) - qty }));
    } else {
      setMaterials(m => ({ ...m, [item.id]: (m[item.id] || 0) - qty }));
    }
    return true;
  }, [getOwned, setVoidCreds, setInventory, setMaterials]);

  return {
    shopItems: displayItems,
    getOwned,
    buyItem,
    sellItem,
    // cooldown
    canReset,
    resetCountdown,
    resetShop,
    cooldownActive: (stats.tradeCooldownHours ?? 24) > 0 && !canReset,
  };
}
